import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@pcmp/react-coworker-components';

import { LoadingFullScreen } from '@/components/helpers/loading/index';
import {
  CodeV2 as Code,
  useGetV2QrCodesBySlugQuery,
} from '@/redux/apis/codes-api';
import { EditableQrCodeForm } from '@/components/page-widgets/codes/detail/form';
import { PreviewQrCode } from '@/components/page-widgets/codes/detail/preview';
import { CopyImageUrlTutorial } from '@/components/tutorials/copy-image-url';
import { DetailedCodeHeader } from '@/components/page-widgets/codes/detail/header';
import { canUserEditCode } from '@/components/page-widgets/codes/detail/permissions';
import {
  CodeProvider,
  UserProvider,
} from '@/components/page-widgets/create-code/context';
import {
  useGetV1ConfigQuery,
  useGetV1UsersMeQuery,
} from '@/redux/apis/frontend-api';
import { useLoading } from '@/components/helpers/loading/context';

const { Container } = Layout;

const QRCode: FC<{
  code: Code;
}> = ({ code }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: userData } = useGetV1UsersMeQuery();
  const { loading } = useLoading();
  const { data: configData } = useGetV1ConfigQuery();

  if (!userData) return <LoadingFullScreen />;
  if (loading) return <LoadingFullScreen textI18nKey="label.duplicatingCode" />;

  const isViewerModeEnabled =
    configData?.maintenanceStatus?.enabled &&
    configData?.maintenanceStatus?.forceViewerMode;
  const canEditCode = canUserEditCode(code, userData);
  const forceViewerMode = isViewerModeEnabled || !canEditCode;

  return (
    <UserProvider user={userData}>
      <CodeProvider code={code}>
        <DetailedCodeHeader />

        <Container className="py-4 ">
          <PreviewQrCode />
          <div
            className={forceViewerMode ? 'pointer-events-none opacity-50' : ''}
          >
            <EditableQrCodeForm />
          </div>
          <CopyImageUrlTutorial />
        </Container>
      </CodeProvider>
    </UserProvider>
  );
};

function CodeDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const effectiveSlug = slug ?? '';
  const {
    data: codeData,
    isLoading: isLoadingCode,
    error,
  } = useGetV2QrCodesBySlugQuery({ slug: effectiveSlug });
  const isLoading = isLoadingCode;

  useEffect(() => {
    if (!effectiveSlug || (error && !codeData && !isLoading)) {
      navigate('/', { replace: true });
    }
  }, [error, codeData, effectiveSlug, isLoading, navigate]);

  if (isLoading || !effectiveSlug) return <LoadingFullScreen />;

  return codeData ? <QRCode code={codeData} /> : null;
}

export default CodeDetailPage;
